import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Swal from 'sweetalert2';

import { deleteRequest, getRequest } from 'Includes/api/apicall';
import { DEL_URL, GET_URL } from 'Includes/urls';

const leaveTypeLabel = (value) => (value === 'work_from_home' ? 'Work from home' : 'Leave');

function toIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function currentMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: toIsoDate(start), to: toIsoDate(end) };
}

function LeaveManagement(props) {
  const monthRange = React.useMemo(() => currentMonthRange(), []);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [fromDate, setFromDate] = React.useState(monthRange.from);
  const [toDate, setToDate] = React.useState(monthRange.to);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [openId, setOpenId] = React.useState(null);

  React.useEffect(() => {
    const handle = setTimeout(() => {
      setLoading(true);
      const params = { staff: 1 };
      if (search.trim()) params.search = search.trim();
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      getRequest(GET_URL.staff_leaves.api, params, props)
        .then((response) => {
          if (response && response.status === 200) {
            const data = response.data?.data || [];
            setRows(Array.isArray(data) ? data : []);
          }
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, fromDate, toDate, reloadKey]);

  const cancelLeave = (leaveId) => {
    deleteRequest(`${DEL_URL.staff_leaves.api}${leaveId}/`, {}, {
      ...props,
      confirmButtonText: 'Yes, cancel leave',
    }).then((res) => {
      if (res?.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Leave cancelled',
          timer: 1400,
          showConfirmButton: false,
        });
        setReloadKey((value) => value + 1);
      }
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <EventNoteIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>Leave Management</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Staff members and their leave and work-from-home history. Open a person to see entries that overlap the selected dates.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Search staff"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="From"
            type="date"
            fullWidth
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="To"
            type="date"
            fullWidth
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {loading ? <CircularProgress size={28} sx={{ m: 2 }} /> : null}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                <TableCell sx={{ width: 48 }} />
                <TableCell sx={{ fontWeight: 700 }}>Staff</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Mobile</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>History</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Alert severity="info" sx={{ my: 1 }}>No staff found.</Alert>
                  </TableCell>
                </TableRow>
              ) : rows.map((person) => {
                const leaves = Array.isArray(person.leaves) ? person.leaves : [];
                const open = openId === person.id;
                return (
                  <React.Fragment key={person.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton size="small" onClick={() => setOpenId(open ? null : person.id)}>
                          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{person.username || '—'}</TableCell>
                      <TableCell>{person.email || '—'}</TableCell>
                      <TableCell>{person.mobile_num || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={`${leaves.length} ${leaves.length === 1 ? 'entry' : 'entries'}`} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1.5, pl: 6 }}>
                            {leaves.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">No leave or work-from-home history.</Typography>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>From</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>To</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                    <TableCell />
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {leaves.map((leave) => (
                                    <TableRow key={leave.id}>
                                      <TableCell>
                                        <Chip
                                          size="small"
                                          label={leaveTypeLabel(leave.leave_type)}
                                          color={leave.leave_type === 'work_from_home' ? 'info' : 'default'}
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell>{leave.from_date}</TableCell>
                                      <TableCell>{leave.to_date}</TableCell>
                                      <TableCell>{leave.reason || '—'}</TableCell>
                                      <TableCell>
                                        <Chip
                                          size="small"
                                          label={leave.status === 'cancelled' ? 'Cancelled' : 'Marked'}
                                          color={leave.status === 'cancelled' ? 'default' : 'warning'}
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell align="right">
                                        {leave.is_active && leave.status !== 'cancelled' ? (
                                          <Button size="small" color="error" onClick={() => cancelLeave(leave.id)}>
                                            Cancel
                                          </Button>
                                        ) : null}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default LeaveManagement;
